import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

function formatINR(n?: number | string | null): string {
  const num = Number(n);
  if (n == null || isNaN(num)) return '₹0.00';
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

export default function PublicEInvoiceVerifyPage() {
  const { irn } = useParams<{ irn: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function fetchVerification() {
      if (!irn) return;
      setLoading(true);
      setError(null);
      try {
        const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/v1/compliance/e-invoice/public-verify/${encodeURIComponent(irn)}`;
        const res = await fetch(apiUrl);
        const json = await res.json();
        if (json?.success && json?.data) {
          setData(json.data);
        } else {
          setError(json?.message || 'Invoice verification details not found');
        }
      } catch (err: any) {
        console.error('Failed to verify E-Invoice:', err);
        setError('Network error while verifying invoice. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchVerification();
  }, [irn]);

  const handleCopyIRN = () => {
    if (data?.irn) {
      navigator.clipboard.writeText(data.irn);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 text-center max-w-sm w-full">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
            <i className="ri-loader-4-line text-3xl animate-spin" />
          </div>
          <h2 className="text-base font-bold text-slate-800">Verifying E-Invoice</h2>
          <p className="text-xs text-slate-500 mt-1">Connecting to GST IRP Verification Portal...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-red-200 text-center max-w-md w-full">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center text-red-600">
            <i className="ri-error-warning-line text-3xl" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Verification Failed</h2>
          <p className="text-xs text-slate-600 mt-1">{error || 'Invalid or non-existent IRN identifier'}</p>
          <div className="mt-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Go to ERP Portal
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isInterstate = data.supplier?.gstin?.slice(0, 2) !== data.buyer?.gstin?.slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-3 sm:px-6 flex flex-col items-center justify-start">
      <div className="w-full max-w-3xl space-y-4">
        
        {/* Top Header & National Verification Banner */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center shadow-md shrink-0">
                <i className="ri-shield-check-fill text-2xl" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-bold text-slate-900 tracking-tight">GST E-Invoice Verification</h1>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 uppercase">
                    IRP Verified ✓
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Official Invoice Registration Portal (IRP) Compliance Record
                </p>
              </div>
            </div>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer self-start sm:self-auto"
            >
              <i className="ri-printer-line" /> Print Document
            </button>
          </div>

          {/* IRN Details Card */}
          <div className="mt-4 bg-emerald-50/60 border border-emerald-200 rounded-xl p-4 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
              <span className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                Invoice Reference Number (IRN)
              </span>
              <button
                onClick={handleCopyIRN}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 cursor-pointer w-fit"
              >
                <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                {copied ? "Copied to Clipboard!" : "Copy IRN"}
              </button>
            </div>
            <div className="p-2.5 bg-white rounded-lg border border-emerald-200/80 font-mono text-xs text-slate-800 break-all select-all font-semibold">
              {data.irn}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 text-xs">
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-medium">Ack Number</span>
                <p className="font-mono font-bold text-slate-800">{data.ackNumber || '—'}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-medium">Ack Date</span>
                <p className="font-semibold text-slate-800">{formatDateTime(data.ackDate)}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-medium">Doc Type</span>
                <p className="font-semibold text-slate-800">{data.documentType || 'TAX INVOICE'}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-medium">Status</span>
                <p className="font-bold text-emerald-700">ACTIVE ✓</p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Summary & Parties */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Supplier Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-2">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1">
                <i className="ri-building-2-line" /> Supplier (Seller)
              </span>
              <span className="text-[10px] font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-600 font-semibold">
                GSTIN: {data.supplier?.gstin}
              </span>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm">{data.supplier?.name}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{data.supplier?.address}</p>
            </div>
            <div className="pt-2 border-t border-slate-50 text-[11px] text-slate-600 flex justify-between">
              <span>PAN: <strong className="font-mono">{data.supplier?.pan || '—'}</strong></span>
              {data.supplier?.phone && <span>Ph: {data.supplier?.phone}</span>}
            </div>
          </div>

          {/* Buyer Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-2">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1">
                <i className="ri-user-shared-line" /> Recipient (Buyer)
              </span>
              <span className="text-[10px] font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-600 font-semibold">
                GSTIN: {data.buyer?.gstin || 'URP (B2C)'}
              </span>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm">{data.buyer?.name}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{data.buyer?.address || '—'}</p>
            </div>
            <div className="pt-2 border-t border-slate-50 text-[11px] text-slate-600 flex justify-between">
              <span>Supply Type: <strong>{isInterstate ? 'Inter-State (IGST)' : 'Intra-State (CGST + SGST)'}</strong></span>
              <span>State Code: <strong className="font-mono">{data.buyer?.stateCode || '—'}</strong></span>
            </div>
          </div>
        </div>

        {/* Invoice Items & Tax Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-slate-900">Document #{data.invoiceNumber}</span>
              <span className="text-xs text-slate-500 font-normal">({formatDate(data.invoiceDate)})</span>
            </div>
            <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100">
              Total: {formatINR(data.totalAmount)}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-600">#</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Item Description</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-600">HSN</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-600">Qty</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-600">Rate</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-600">Taxable</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-600">GST%</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Array.isArray(data.items) && data.items.length > 0 ? (
                  data.items.map((item: any, idx: number) => (
                    <tr key={idx} className={idx % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}>
                      <td className="px-3 py-2.5 text-slate-400">{idx + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-slate-900">{item.name}</td>
                      <td className="px-3 py-2.5 font-mono text-slate-600">{item.hsn}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{item.qty} {item.unit || ''}</td>
                      <td className="px-3 py-2.5 text-right">{formatINR(item.rate)}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-slate-700">{formatINR(item.taxable)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600">{item.taxRate}%</td>
                      <td className="px-3 py-2.5 text-right font-bold text-slate-900">{formatINR(item.total)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-slate-400">
                      Standard Commercial Goods & Services
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals Summary */}
          <div className="bg-slate-50 border-t border-slate-200 px-5 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="text-xs text-slate-500 space-y-0.5">
              <p>Digitally signed by Invoice Registration Portal (IRP).</p>
              <p>Valid proof of GST invoice registration for Input Tax Credit (ITC).</p>
            </div>
            <div className="text-right space-y-1 self-end sm:self-auto">
              <span className="text-xs text-slate-500 font-medium mr-2">Grand Total:</span>
              <span className="text-lg font-extrabold text-slate-900">{formatINR(data.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Official Verification Footer */}
        <div className="text-center text-xs text-slate-400 py-3 space-y-1">
          <p>Verified at {formatDateTime(data.verifiedAt)} • GST System India</p>
          <p className="text-[11px]">InvenPro ERP Compliance Engine • Digi India Solutions</p>
        </div>

      </div>
    </div>
  );
}
